// Interface sound control (docs/specs/site-sound.md §7).
//
// Sound defaults ON, so the mute has to be reachable at any scroll depth — this
// lives in the sticky header, which is why it is icon-only and quiet rather than
// a second control competing with the page's one CTA.
//
// The audit decks get their own equivalent, injected rather than rendered
// (app/services/site_sound/deck_bindings.js), because a deck is a self-contained
// document with no React. Both write the SAME preference key, so a prospect who
// mutes once is muted everywhere on the domain.
import * as React from "react"
import { usePage } from "@inertiajs/react"
import { Volume2, VolumeX } from "lucide-react"
import {
  getServerSoundSnapshot,
  isSoundEnabled,
  setSoundEnabled,
  subscribeSound,
} from "@/lib/sound"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

export function SoundToggle({ className }: { className?: string }) {
  // The sitewide kill switch (/admin/controls). Read from shared props rather
  // than from the core, because this has to be right on the very first render —
  // before any gesture has created an audio context to ask.
  const available = usePage<PageProps>().props.sound?.enabled ?? true

  // The preference lives outside React (localStorage, shared with every deck on
  // the domain, and changeable from another tab), so it is read as an external
  // store rather than mirrored into state. getServerSoundSnapshot keeps SSR and
  // the first client paint in agreement.
  const enabled = React.useSyncExternalStore(
    subscribeSound,
    isSoundEnabled,
    getServerSoundSnapshot,
  )

  // Sound is off sitewide: render nothing. A toggle that cannot make a sound is
  // worse than no toggle — it invites a visitor to fix something that is not
  // broken. Their stored preference is untouched and returns when Matt does.
  if (!available) return null

  const Icon = enabled ? Volume2 : VolumeX
  const label = enabled ? "Sound on" : "Sound off"

  return (
    <button
      type="button"
      aria-pressed={enabled}
      aria-label="Sound"
      title={label}
      // Turning sound OFF cannot announce itself, so the icon is the required
      // signal rather than a decoration (§8).
      onClick={() => setSoundEnabled(!enabled)}
      // How often visitors mute is worth knowing, and the existing analytics
      // delegation picks this up for free.
      data-track-click={enabled ? "sound_off" : "sound_on"}
      className={cn(
        "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted transition-colors hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        className,
      )}
    >
      <Icon aria-hidden="true" className="h-4 w-4" />
    </button>
  )
}
